// Global Instructions Rule Applied!
//
// Fund full-form Distributions tab (docs/WATERFALL_CARRY_DESIGN.md, phase 3):
// the fund's distributions on the lightweight fetch-and-render recipe of the
// Capital Calls tab - one FormSection, the row link opens the distribution
// form, New Distribution opens it pre-scoped to the fund. A row of a
// consolidated distribution (waterfall phase 5) is tagged as its lead (the
// primary fund's row, the inputs and the lifecycle) or as an entity row; the
// form opens it in consolidated mode either way.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import { useTabManagerContext } from '../../../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_DISTRIBUTIONS } from '../../../../../state_manager/globals.ts'
import { fetchFundDistributions } from '../../../../distributions/services/distributionsService.ts'
import type { DistributionListRow } from '../../../../../state_manager/data_models/distribution_detail.ts'
import { getFundNameById } from '../../../../../core/utils/lookupLabelFromStore.ts'
import ExportRowsButton from '../../../../shared/table_grid_export/ExportRowsButton.tsx'

const { Text } = Typography

const disp = (v: unknown): string => (v != null && v !== '' ? String(v) : '-')

interface DistributionsTabProps {
  fundId?: string | null
}

export default function DistributionsTab({ fundId }: DistributionsTabProps) {
  const { openFormInTab } = useTabManagerContext()
  const [rows, setRows] = useState<DistributionListRow[]>([])
  const [loading, setLoading] = useState(false)

  const refetch = useCallback(async () => {
    if (!fundId) return
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundDistributions(fundId, token)
      if (res.error) showErrorToast(res.error)
      setRows(res.data)
    } finally {
      setLoading(false)
    }
  }, [fundId])

  useEffect(() => {
    void refetch()
  }, [refetch])

  const columns = useMemo(
    () => [
      {
        title: 'Date',
        dataIndex: 'distributions__distribution_date_fmt',
        width: 110,
        render: (v: string, row: DistributionListRow) => (
          <button
            type='button'
            className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
            onClick={() => openFormInTab(TABLE_DISTRIBUTIONS, row.distributions__id, row.distributions__name || 'Distribution')}
          >
            {disp(v)}
          </button>
        )
      },
      {
        title: 'Name',
        dataIndex: 'distributions__name',
        render: (v: string | null, row: DistributionListRow) => (
          <span>
            {disp(v)}
            {row.prog__consolidated_role ? (
              <Tooltip title={row.fund_groups__name ? `Consolidated distribution of ${row.fund_groups__name}` : undefined}>
                <Tag color='purple' className='ml-2'>
                  {row.prog__consolidated_role === 'lead' ? 'Consolidated' : 'Consolidated · entity'}
                </Tag>
              </Tooltip>
            ) : null}
          </span>
        )
      },
      { title: 'Kind', dataIndex: 'cfg_distribution_kinds__label', width: 200, render: disp },
      {
        title: 'Status',
        dataIndex: 'cfg_distribution_statuses__label',
        width: 90,
        render: (v: string | null, row: DistributionListRow) =>
          v ? (
            <span>
              <Tag color={v === 'Issued' ? 'green' : 'blue'}>{v}</Tag>
              {v === 'Draft' && row.distributions__needs_compute ? <Tag color='gold'>Needs Compute</Tag> : null}
            </span>
          ) : (
            '-'
          )
      },
      { title: 'Gross', dataIndex: 'distributions__gross_amount_fmt', width: 130, align: 'right' as const, render: disp },
      { title: 'Lines total', dataIndex: 'prog__effective_total_fmt', width: 130, align: 'right' as const, render: disp },
      { title: 'Lines', dataIndex: 'prog__line_count', width: 70, align: 'right' as const, render: disp },
      {
        title: 'Booking',
        dataIndex: 'cfg_transaction_statuses__label',
        width: 90,
        render: (v: string | null) => (v ? <Tag color={v === 'Booked' ? 'green' : 'blue'}>{v}</Tag> : '-')
      }
    ],
    [openFormInTab]
  )

  if (!fundId) {
    return <div className='p-6 text-gray-500'>Save the fund first to manage distributions.</div>
  }

  return (
    <div className='h-full overflow-y-auto p-3 flex flex-col gap-3'>
      <FormSection
        title='Distributions'
        titleColor='blue'
        loading={loading}
        headerActions={
          <Space>
            <ExportRowsButton
              name={`${getFundNameById(fundId) || 'Fund'} Distributions`}
              rows={rows}
              columns={[
                { title: 'Date', value: (r) => r.distributions__distribution_date, kind: 'date' },
                { title: 'Name', value: (r) => r.distributions__name },
                {
                  title: 'Consolidated',
                  value: (r) =>
                    r.prog__consolidated_role === 'lead'
                      ? 'Consolidated'
                      : r.prog__consolidated_role === 'entity'
                        ? 'Consolidated · entity'
                        : null
                },
                { title: 'Kind', value: (r) => r.cfg_distribution_kinds__label },
                { title: 'Status', value: (r) => r.cfg_distribution_statuses__label },
                {
                  title: 'Needs Compute',
                  value: (r) => (r.cfg_distribution_statuses__label === 'Draft' && r.distributions__needs_compute ? 'Yes' : null)
                },
                { title: 'Gross', value: (r) => r.distributions__gross_amount },
                { title: 'Lines total', value: (r) => r.prog__effective_total },
                { title: 'Lines', value: (r) => r.prog__line_count },
                { title: 'Booking', value: (r) => r.cfg_transaction_statuses__label }
              ]}
            />
            <Button
              size='small'
              type='primary'
              icon={<PlusOutlined />}
              onClick={() => openFormInTab(TABLE_DISTRIBUTIONS, null, 'New Distribution', { fund: fundId })}
            >
              New Distribution
            </Button>
            <Button size='small' icon={<ReloadOutlined />} onClick={() => void refetch()}>
              Refresh
            </Button>
          </Space>
        }
      >
        {rows.length === 0 ? (
          <Text type='secondary'>No distributions for this fund yet.</Text>
        ) : (
          <Table size='small' columns={columns} dataSource={rows} pagination={false} rowKey='distributions__id' />
        )}
      </FormSection>
    </div>
  )
}
